import React, { useEffect, useState, useTransition } from 'react';
import {
	Autocomplete,
	Card,
	Chip,
	FormControl,
	FormHelperText,
	FormLabel,
	IconButton,
	Radio,
	Skeleton,
	TextField,
	Tooltip,
	Typography,
} from '@mui/material';
import { KeyphrasesInfo } from '../../types/upload';
import { Stack } from '@mui/system';
import { Clear, FilterListOff } from '@mui/icons-material';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';

interface KeyphraseProps {
	selectedList?: KeyphrasesInfo;
	setSelectedList: (e: KeyphrasesInfo | undefined) => void;
	errorText?: string;
	keyphraseOptions: KeyphrasesInfo[];
	helperText: string;
	showPipelines?: boolean;
	isLoading?: boolean;
}

export const KeyphraseSelect: React.FC<KeyphraseProps> = ({
	selectedList,
	setSelectedList,
	errorText,
	keyphraseOptions,
	helperText,
	showPipelines = false,
	isLoading = false,
}) => {
	const [filteredLists, setFilteredLists] = useState<KeyphrasesInfo[]>([...keyphraseOptions]);
	const [nameFilter, setNameFilter] = useState<string>('');
	const [useCaseFilter, setUseCaseFilter] = useState<string>('');

	const [refreshingData, startTransition] = useTransition();

	const useCaseOptions = Array.from(new Set(keyphraseOptions.map((opt) => opt.useCase)));

	const headCells: GridColDef[] = [
		{
			field: 'title',
			headerName: 'Keyphrase List Name',
			sortable: true,
			flex: 2,
			display: 'flex',
			renderCell: (params: GridRenderCellParams) => (
				<Stack data-testid={`keyphrase-details-row-${params.row.title}`} direction="row" gap={1} alignItems="center">
					<Radio
						slotProps={{
							input: {
								'aria-label': params.row.title,
							},
						}}
						checked={selectedList === params.row}
					/>
					<Typography>{params.row.title}</Typography>
					{params.row === selectedList && <Chip label="Selected" color="primary" size="small" />}
				</Stack>
			),
		},
		{
			field: 'useCase',
			headerName: 'Use Case',
			sortable: true,
			flex: 1,
			display: 'flex',
			renderCell: (params: GridRenderCellParams) => <Typography>{params.row.useCase}</Typography>,
		},
		...(showPipelines
			? [
					{
						field: 'pipeline',
						headerName: 'Pipeline',
						sortable: true,
						flex: 1,
						display: 'flex' as const,
						renderCell: (params: GridRenderCellParams) => <Typography>{params.row.pipeline}</Typography>,
					},
				]
			: []),
		{
			field: 'lastModified',
			headerName: 'Last Modified',
			sortable: true,
			flex: 1,
			display: 'flex',
			renderCell: (params: GridRenderCellParams) => <Typography component="span">{params.row.lastModified}</Typography>,
		},
	];

	const refreshData = (newNameFilter: string, newUseCaseFilter: string) => {
		let newLists = [...keyphraseOptions];
		if (newNameFilter) {
			newLists = newLists.filter((list) => list.title.toLocaleLowerCase().includes(newNameFilter.toLocaleLowerCase()));
		}
		if (newUseCaseFilter) {
			newLists = newLists.filter((list) => list.useCase === newUseCaseFilter);
		}

		if (!newLists.find((list) => list.id === selectedList?.id)) {
			setSelectedList(undefined);
		}
		setFilteredLists(newLists);
	};

	const updateFilters = (newVal: string, filterType: 'name' | 'useCase') => {
		let newNameFilter = nameFilter;
		let newUseCaseFilter = useCaseFilter;

		switch (filterType) {
			case 'name': {
				setNameFilter(newVal);
				newNameFilter = newVal;
				break;
			}
			case 'useCase': {
				setUseCaseFilter(newVal);
				newUseCaseFilter = newVal;
				break;
			}
		}
		refreshData(newNameFilter, newUseCaseFilter);
	};

	const resetFilters = () => {
		setFilteredLists([...keyphraseOptions]);
		setNameFilter('');
		setUseCaseFilter('');
	};

	useEffect(() => {
		startTransition(() => {
			refreshData(nameFilter, useCaseFilter);
		});
	}, [keyphraseOptions]);

	return (
		<Stack width={'100%'} spacing={1}>
			<FormLabel sx={{ padding: '5px' }}>{helperText}</FormLabel>
			{isLoading || refreshingData ? (
				<Tooltip title={'Keyphrase Lists Loading'}>
					<Stack spacing={1} role="img">
						<Skeleton variant="rectangular" width={'100%'} height="4em" />
						<Skeleton variant="rectangular" width={'100%'} height="4em" />
					</Stack>
				</Tooltip>
			) : (
				<Stack gap={1} sx={errorText ? { borderWidth: '1px', borderColor: 'error.main', borderStyle: 'solid' } : undefined}>
					<Card variant="outlined">
						<Stack direction="row" margin={1} gap={2}>
							<TextField
								fullWidth
								size="small"
								variant="outlined"
								label="Filter by Title"
								value={nameFilter}
								onChange={(e) => {
									updateFilters(e.target.value, 'name');
								}}
								slotProps={{
									input: {
										endAdornment: (
											<IconButton
												onClick={() => updateFilters('', 'name')}
												disabled={!nameFilter}
												data-testid={'clear-name-button'}
												aria-label="Clear Name Search Button"
											>
												<Clear />
											</IconButton>
										),
									},
								}}
								data-testid={'search-title-textfield'}
							/>
							<FormControl fullWidth>
								<Autocomplete
									size="small"
									value={useCaseFilter}
									onChange={(_, newVal) => updateFilters(newVal || '', 'useCase')}
									options={useCaseOptions}
									fullWidth
									renderInput={(params) => (
										<TextField
											label="Filter by Use Case"
											{...params}
											slotProps={{
												htmlInput: { ...params.inputProps, 'aria-label': `${useCaseFilter} Filter` },
											}}
										/>
									)}
									data-testid={`use-case-filter-selector`}
								/>
							</FormControl>
							<Tooltip title={'Clear Filters'}>
								<IconButton onClick={() => resetFilters()} data-testid={`reset-filters-button`}>
									<FilterListOff />
								</IconButton>
							</Tooltip>
						</Stack>
					</Card>
					<DataGrid
						key={'keyphraseSelect'}
						rows={filteredLists}
						columns={headCells}
						disableColumnFilter
						localeText={{
							noRowsLabel: 'No keyphrase lists were found',
							footerRowSelected: () => `${selectedList?.title} selected`,
							paginationRowsPerPage: 'Keyphrase lists per page:',
							paginationDisplayedRows: (params) =>
								params.to === params.count
									? `Showing all available keyphrase lists`
									: `Showing ${params.from} to ${params.to} out of ${params.count} keyphrase lists`,
						}}
						initialState={{
							pagination: {
								paginationModel: { pageSize: 5, page: 0 },
							},
						}}
						sx={{
							'& .MuiDataGrid-row': {
								minHeight: '6ch !important',
							},
							'& .MuiDataGrid-row.Mui-selected': {
								backgroundColor: 'background.paper',
							},
							'& .MuiDataGrid-row.Mui-selected:hover': {
								backgroundColor: 'background.paper',
							},
						}}
						getRowHeight={() => 'auto'}
						autoHeight
						disableMultipleRowSelection
						onRowClick={(row) => (selectedList === row.row ? setSelectedList(undefined) : setSelectedList(row.row))}
						disableColumnMenu
						pageSizeOptions={[5]}
					/>
				</Stack>
			)}
			<FormHelperText data-testid="keyphrase-selector-helper-text" sx={{ color: 'error.main' }}>
				{errorText}
			</FormHelperText>
		</Stack>
	);
};
